import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Printer, History, FileText } from "lucide-react";
import { Patient } from "@/types";
import { useState, useEffect } from "react";
import { BillGenerationDialog } from "./BillGenerationDialog";
import { BillHistoryDialog } from "./BillHistoryDialog";
import { formatPatientId } from '@/lib/utils/patientId';
import { format } from 'date-fns';
import { useBillsStore } from "@/lib/stores/bills";

interface PatientPaymentSummaryProps {
  patient: Patient;
}

export function PatientPaymentSummary({ patient }: PatientPaymentSummaryProps) {
  const [isBillDialogOpen, setIsBillDialogOpen] = useState(false);
  const [isBillHistoryOpen, setIsBillHistoryOpen] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const { bills, fetchBills, getBillsByPatient } = useBillsStore();


  // Calculate services cost with proper null checks
  const servicesCost = patient.services?.reduce((total, service) => {
    const price = Number(service.price) || 0;
    return total + price;
  }, 0) || 0;
  
  // Calculate total amount paid from payment history (more reliable than amountPaid field)
  const totalAmountPaid = (patient.paymentHistory || []).reduce((total, payment) => {
    return total + (Number(payment.amount) || 0);
  }, 0);
  
  // Use the calculated amount paid, fallback to the stored amountPaid if paymentHistory is empty
  const amountPaid = totalAmountPaid > 0 ? totalAmountPaid : (Number(patient.amountPaid) || 0);
  
  // Calculate total cost and remaining amount with proper null checks
  const totalCost = servicesCost;
  const remainingAmount = Math.max(0, totalCost - amountPaid);

  // Get payment history
  const paymentHistory = patient.paymentHistory || [];

  // Fetch bills for this patient
  useEffect(() => {
    fetchBills(patient.id);
  }, [patient.id, fetchBills]);

  // Get bills for this patient
  const patientBills = getBillsByPatient(patient.id);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Payment Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">


          {/* Services */}
          {patient.services && patient.services.length > 0 && (
            <div className="space-y-2">
              <div className="font-medium">Services</div>
              {patient.services.map((service) => (
                <div key={service.id} className="flex justify-between text-sm">
                  <span>{service.service_name}</span>
                  <span>₹{(Number(service.price) || 0).toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm font-medium">
                <span>Total Services</span>
                <span>₹{servicesCost.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Total Cost */}
          <div className="border-t pt-2 flex justify-between">
            <span className="font-medium">Total Cost</span>
            <span>₹{totalCost.toFixed(2)}</span>
          </div>



          {/* Amount Paid */}
          <div className="flex justify-between">
            <span className="font-medium">Amount Paid</span>
            <span>₹{amountPaid.toFixed(2)}</span>
          </div>

          {/* Payment Mode */}
          {patient.paymentMode && (
            <div className="flex justify-between text-sm">
              <span>Payment Mode</span>
              <span className="capitalize">{patient.paymentMode}</span>
            </div>
          )}

          {/* Remaining Amount */}
          <div className="flex justify-between">
            <span className="font-medium">Remaining Amount</span>
            <span className={remainingAmount > 0 ? "text-red-600" : "text-green-600"}>
              ₹{remainingAmount.toFixed(2)}
            </span>
          </div>

          {/* Patient ID */}
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-medium">Patient ID</span>
            <span className="bg-hospital-primary/10 px-3 py-1 rounded-full text-hospital-primary font-medium text-sm">
              {formatPatientId(patient.id)}
            </span>
          </div>

          {/* Payment History */}
          {paymentHistory.length > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="font-medium">Payment History</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPaymentHistory(!showPaymentHistory)}
                >
                  <History className="mr-2 h-4 w-4" />
                  {showPaymentHistory ? "Hide" : "Show"} History
                </Button>
              </div>
              {showPaymentHistory && (
                <div className="space-y-2 text-sm">
                  {paymentHistory.map((payment, index) => {
                    // Calculate running total up to this payment
                    const runningTotal = paymentHistory
                      .slice(0, index + 1)
                      .reduce((total, p) => total + (Number(p.amount) || 0), 0);
                    
                    return (
                      <div key={index} className="flex justify-between items-center border-b border-gray-100 pb-1">
                        <div className="flex flex-col">
                          <span className="font-medium">{format(new Date(payment.date), "dd MMM yyyy, HH:mm")}</span>
                          <span className="text-xs text-gray-500 capitalize">{payment.mode}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-medium">₹{payment.amount.toFixed(2)}</span>
                          <div className="text-xs text-gray-500">Total: ₹{runningTotal.toFixed(2)}</div>
                        </div>
                      </div>
                    );
                  })}
                  {paymentHistory.length > 0 && (
                    <div className="pt-2 border-t border-gray-200">
                      <div className="flex justify-between font-medium">
                        <span>Grand Total Paid:</span>
                        <span className="text-green-600">₹{amountPaid.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Generate Bill Button */}
          <Button 
            className="w-full" 
            variant="outline"
            onClick={() => setIsBillDialogOpen(true)}
          >
            <Printer className="mr-2 h-4 w-4" />
            Generate Bill
          </Button>

          {/* View Bills Button */}
          <Button 
            className="w-full" 
            variant="outline"
            onClick={() => setIsBillHistoryOpen(true)}
          >
            <FileText className="mr-2 h-4 w-4" />
            View Bills ({patientBills.length})
          </Button>
        </CardContent>
      </Card>

      {/* Bill Generation Dialog */}
      <BillGenerationDialog
        isOpen={isBillDialogOpen}
        onClose={() => setIsBillDialogOpen(false)}
        patient={patient}
      />

      {/* Bill History Dialog */}
      <BillHistoryDialog
        isOpen={isBillHistoryOpen}
        onClose={() => setIsBillHistoryOpen(false)}
        bills={patientBills}
        patientName={patient.name}
      />
    </>
  );
}
